import { atom } from "jotai"
import { store } from "@/store/store"
import { showingMaskingPanelAtom } from "@/store/ui-views-state"
import {
	maskOverlaySettingsAtom,
	selectedMaskGroupAtom,
	showingFloatingMaskingPanelAtom,
} from "@/store/editor/masks-state"
import { colorPickerSessionAtom } from "@/store/color-picker-state"
import type { Mask } from "@/core/types"

export { maskOverlaySettingsAtom } from "@/store/editor/masks-state"

export interface MaskOverlayPreviewState {
	ownerId: string | null
	masks: Mask[]
}

export type MaskOverlayPreviewAction =
	| { type: "set"; ownerId: string; masks: Mask[] }
	| { type: "clear"; ownerId: string }
	| { type: "reset" }

const emptyMaskOverlayPreviewState = (): MaskOverlayPreviewState => ({
	ownerId: null,
	masks: [],
})

const maskOverlayPreviewValueAtom = atom<MaskOverlayPreviewState>(emptyMaskOverlayPreviewState())

/**
 * Owner-scoped preview writes prevent a late cleanup from an old menu/popover instance from
 * clearing the preview published by its replacement.
 */
export const maskOverlayPreviewStateAtom = atom(
	(get) => get(maskOverlayPreviewValueAtom),
	(get, set, action: MaskOverlayPreviewAction) => {
		if (action.type === "reset") {
			set(maskOverlayPreviewValueAtom, emptyMaskOverlayPreviewState())
			return
		}

		const current = get(maskOverlayPreviewValueAtom)
		if (action.type === "clear") {
			if (current.ownerId === action.ownerId) {
				set(maskOverlayPreviewValueAtom, emptyMaskOverlayPreviewState())
			}
			return
		}

		if (action.masks.length === 0) {
			if (current.ownerId === action.ownerId) {
				set(maskOverlayPreviewValueAtom, emptyMaskOverlayPreviewState())
			}
			return
		}

		set(maskOverlayPreviewValueAtom, {
			ownerId: action.ownerId,
			masks: [...action.masks],
		})
	},
)

/**
 * Overlay orchestration consumes only the current mask array. Preview publishers use the
 * owner-scoped action atom above.
 */
export const maskOverlayPreviewMasksAtom = atom((get) => get(maskOverlayPreviewStateAtom).masks)

const hoveringMaskOverlayId = atom<string | null>(null)

export const maskOverlayIdAtom = atom((get) => {
	const colorPickerSession = get(colorPickerSessionAtom)
	// Keep the image unobstructed while sampling a Point Color.
	if (colorPickerSession?.purpose === "point-color") {
		return null
	}

	const settings = get(maskOverlaySettingsAtom)
	const selectedMaskGroup = get(selectedMaskGroupAtom)
	const isMaskingModeActive = get(showingMaskingPanelAtom)

	// showingFloatingMaskingPanelAtom is set when mask adjustment sliders are dragged
	// or when mask overlay UI is being dragged
	const isAdjustingMask = !get(showingFloatingMaskingPanelAtom) && !settings.previewAdjustedMask

	const selectedMaskGroupId = selectedMaskGroup?.properties.id
	const hoveringMaskId = get(hoveringMaskOverlayId)

	// Never show overlays when we are not in masking mode
	if (!isMaskingModeActive) {
		return null
	}

	// Temporarily hide overlays while sliders are being adjusted
	if (isAdjustingMask) {
		return null
	}

	if (!settings.showOverlay) {
		return null
	}

	return hoveringMaskId ?? selectedMaskGroupId
})

export const setMaskOverlay = (maskId: string | null) => {
	store.set(hoveringMaskOverlayId, maskId)
}
